<template>
  <div v-if="!loading">
    <div class="column is-12">
      <h2 class="subtitle" style="margin-top: 16px;">
        <span v-if="formattedDateRange">{{ formattedDateRange }}</span>
        <span v-else>Registro de actividad — histórico</span>
      </h2>
    </div>

    <div class="column is-12">
      <div class="box">
        <p class="subtitle">Días / Horas que más se cargan productos</p>
        <template v-if="hasUploadsData">
          <apexchart type="bar" :options="optionsUploads" :series="seriesUploads" height="350"></apexchart>
        </template>
        <div v-else class="empty-state">
          <span class="material-icons empty-state-icon">upload</span>
          <p>Sin registros en este rango de fechas</p>
        </div>
      </div>
    </div>

    <div class="column is-12">
      <div class="box">
        <p class="subtitle">Días / Horas que más compramos productos</p>
        <template v-if="hasPurchasesData">
          <apexchart type="bar" :options="optionsPurchases" :series="seriesPurchases" height="350"></apexchart>
        </template>
        <div v-else class="empty-state">
          <span class="material-icons empty-state-icon">shopping_cart</span>
          <p>Sin registros en este rango de fechas</p>
        </div>
      </div>
    </div>
  </div>
  <div v-else>
    <loading-component />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import moment from 'moment'

export default {
  name: 'activity-charts',
  data () {
    return {
      loading: false,
      startDate: null,
      endDate: null,
      seriesUploads: [],
      optionsUploads: {
        chart: { type: 'bar', height: 350, toolbar: { show: true } },
        plotOptions: { bar: { horizontal: false, columnWidth: '60%' } },
        xaxis: { categories: [] },
        legend: { position: 'top' },
        colors: ['#9A2EFE']
      },
      seriesPurchases: [],
      optionsPurchases: {
        chart: { type: 'bar', height: 350, toolbar: { show: true } },
        plotOptions: { bar: { horizontal: false, columnWidth: '60%' } },
        xaxis: { categories: [] },
        legend: { position: 'top' },
        colors: ['#00FF96']
      }
    }
  },
  props: {
    dateSegmentationGraph: { type: Object, default: () => ({}) }
  },
  computed: {
    formattedDateRange () {
      if (this.startDate && this.endDate) {
        const start = moment(this.startDate).format('DD-MM-YYYY')
        const end = moment(this.endDate).format('DD-MM-YYYY')
        return start === end
          ? `Rango de fecha: ${start} (por hora)`
          : `Rango de fecha: ${start} / ${end}`
      }
      return null
    },
    hasUploadsData () {
      return this.seriesUploads.some(s => s.data && s.data.some(v => v > 0))
    },
    hasPurchasesData () {
      return this.seriesPurchases.some(s => s.data && s.data.some(v => v > 0))
    }
  },
  methods: {
    ...mapActions(['getActivityCharts', 'getActivityChartsSegmentation']),

    async loadGeneral () {
      this.loading = true
      try {
        const result = await this.getActivityCharts()
        if (!result) return

        const uploads = result.productUploadsChart || []
        const purchases = result.productPurchasesChart || []

        const uploadCategories = uploads.map(d => d.unit || 'Sin fecha')
        const purchaseCategories = purchases.map(d => d.unit || 'Sin fecha')

        this.seriesUploads = [{ name: 'Productos cargados', data: uploads.map(d => Number(d.total)) }]
        this.optionsUploads = { ...this.optionsUploads, xaxis: { categories: uploadCategories } }

        this.seriesPurchases = [{ name: 'Productos comprados', data: purchases.map(d => Number(d.total)) }]
        this.optionsPurchases = { ...this.optionsPurchases, xaxis: { categories: purchaseCategories } }
      } catch (err) {
        console.error('Error al obtener activity charts:', err)
      } finally {
        this.loading = false
      }
    },

    async loadSegmentation (dateObj) {
      this.loading = true
      try {
        const result = await this.getActivityChartsSegmentation(dateObj)
        if (!result) return

        const uploads = result.productUploadsChart || []
        const purchases = result.productPurchasesChart || []

        const uploadCategories = uploads.map(d => d.unit || 'Sin fecha')
        const purchaseCategories = purchases.map(d => d.unit || 'Sin fecha')

        this.seriesUploads = [{ name: 'Productos cargados', data: uploads.map(d => Number(d.total)) }]
        this.optionsUploads = { ...this.optionsUploads, xaxis: { categories: uploadCategories } }

        this.seriesPurchases = [{ name: 'Productos comprados', data: purchases.map(d => Number(d.total)) }]
        this.optionsPurchases = { ...this.optionsPurchases, xaxis: { categories: purchaseCategories } }
      } catch (err) {
        console.error('Error al obtener activity charts segmentation:', err)
      } finally {
        this.loading = false
      }
    },

    handleDateChange (newData) {
      if (newData && newData.start && newData.end) {
        const dateObj = {
          start: moment(newData.start).startOf('day').format('YYYY-MM-DD HH:mm:ss'),
          end: moment(newData.end).endOf('day').format('YYYY-MM-DD HH:mm:ss')
        }
        this.startDate = newData.start
        this.endDate = newData.end
        this.loadSegmentation(dateObj)
      } else {
        this.startDate = null
        this.endDate = null
        this.loadGeneral()
      }
    }
  },
  watch: {
    dateSegmentationGraph (newData) {
      this.handleDateChange(newData)
    }
  },
  mounted () {
    this.handleDateChange(this.dateSegmentationGraph)
  },
  components: {
    LoadingComponent: () => import('@/components/ui/Loading')
  }
}
</script>

<style scoped>
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
  color: #aaa;
}
.empty-state-icon {
  font-size: 48px;
  margin-bottom: 12px;
  opacity: 0.4;
}
.empty-state p {
  font-size: 15px;
  margin: 0;
}
</style>
